import type { ReactNode } from "react";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { Permission, userHasAnyPermission, userHasPermission } from "~/domains/user/utils/permissions";
import { isKITProduct } from "~/domains/product/types/ProductData";
import { getIsContractAvailable } from "~/features/productDetails/redux/productDetailsSelectors";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { type AtcCapabilities, type AtcDescriptorFacts, type AtcScreen } from "~/features/addToCart/flow/types";
import {
  type CloseIntent,
  getAtcAutoSubmitKey,
  isSubmittableAtcScreen,
  useAtcFlowController,
} from "~/features/addToCart/flow/useAtcFlowController";
import {
  type AddToCartHookProps,
  type AddToCartSubmitData,
  type UseAddToCartFormReturn,
  useAddToCartForm,
} from "~/features/addToCart/hooks/useAddToCartForm";
import { AddToCartDerivedContextProvider } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import {
  AddToCartFlowProvider,
  type AddToCartFlowContextValue,
} from "~/features/addToCart/components/AddToCartProcess/AddToCartFlowContext";

export type AddToCartSubmitHandler = (data: AddToCartSubmitData) => Promise<boolean>;

function createAtcDescriptorFacts(productDescriptor: ProductDescriptor): AtcDescriptorFacts {
  return {
    isLoaded: productDescriptor.isLoaded,
    isError: productDescriptor.isError,
    quantityVisible: productDescriptor.base.quantityVisible,
    hasAvailability: !!productDescriptor.availability,
    hasContractAvailable: getIsContractAvailable(productDescriptor),
    isKitWithSelectableParts: isKitWithSelectableParts(productDescriptor),
  };
}

function createAtcCapabilities() {
  return {
    canViewAvailability: userHasPermission(Permission.AVAILABILITY),
    canViewPrice: userHasPermission(Permission.PRICE),
    canSubmitToCartOrQuote: userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE),
    canSeeAddToCartDisclaimer: userHasPermission(Permission.ADD_TO_CART),
  };
}

function getAutoSubmitKey(
  flowKind: "addToCart" | "editPart",
  resolvedInteractiveScreen: AtcScreen,
  descriptorFacts: AtcDescriptorFacts,
) {
  if (!isSubmittableAtcScreen(resolvedInteractiveScreen)) {
    return null;
  }

  return getAtcAutoSubmitKey({
    flowKind,
    screen: resolvedInteractiveScreen,
    quantityVisible: descriptorFacts.quantityVisible,
  });
}

function getResolvedInteractiveScreen(resolvedScreen: AtcScreen, descriptorFacts: AtcDescriptorFacts): AtcScreen {
  if (resolvedScreen === "contractAvailable") {
    return descriptorFacts.isKitWithSelectableParts ? "kitSummary" : "defaultForm";
  }

  return resolvedScreen;
}

function isKitWithSelectableParts(productDescriptor: ProductDescriptor) {
  if (!isKITProduct(productDescriptor.base)) {
    return false;
  }

  const { includedParts, optionalParts } = productDescriptor.base;
  return includedParts.length > 0 || optionalParts.length > 0;
}

type AtcWizardFlowKind = "addToCart" | "editPart";

interface AtcWizardResolveScreenParams {
  capabilities: AtcCapabilities;
  descriptorFacts: AtcDescriptorFacts;
}

interface UseAtcWizardProcessParams {
  flowKind: AtcWizardFlowKind;
  hookProps: AddToCartHookProps;
  onRequestClose: (intent: CloseIntent) => void;
  onSubmit: AddToCartSubmitHandler;
  resolveScreen: (params: AtcWizardResolveScreenParams) => AtcScreen;
  restrictionQuery: string;
}

export interface AtcWizardProcessResult {
  canSeeAddToCartDisclaimer: boolean;
  canSubmitToCartOrQuote: boolean;
  currentScreen: AtcScreen;
  derivedCtx: Pick<UseAddToCartFormReturn, "main" | "initialPackCodeResolved" | "optionalParts" | "prepareSubmitData">;
  flowContextProps: AddToCartFlowContextValue;
  form: UseAddToCartFormReturn["form"];
  isSending: boolean;
  productDescriptor: ProductDescriptor;
  submit: () => void;
}

export function useAtcWizardProcess({
  flowKind,
  hookProps,
  onRequestClose,
  onSubmit,
  resolveScreen,
  restrictionQuery,
}: UseAtcWizardProcessParams): AtcWizardProcessResult {
  const { form, productDescriptor, main, initialPackCodeResolved, optionalParts, prepareSubmitData } =
    useAddToCartForm(hookProps);
  const editMode = hookProps.editMode;

  const capabilities = useMemo(() => createAtcCapabilities(), []);
  const descriptorFacts = useMemo(() => createAtcDescriptorFacts(productDescriptor), [productDescriptor]);
  const resolvedScreen = resolveScreen({ descriptorFacts, capabilities });
  const resolvedInteractiveScreen = getResolvedInteractiveScreen(resolvedScreen, descriptorFacts);

  const { currentScreen, isSending, continueWithoutContract, markContractLoadErrored, requestClose, submit } =
    useAtcFlowController({
      flowKind,
      resolvedScreen,
      resolvedInteractiveScreen,
      prepareSubmitData,
      onSubmit,
      onRequestClose,
      autoSubmitKey: getAutoSubmitKey(flowKind, resolvedInteractiveScreen, descriptorFacts),
    });

  const flowContextProps = useMemo<AddToCartFlowContextValue>(
    () => ({
      continueWithoutContract,
      currentScreen,
      editMode: editMode ?? false,
      markContractLoadErrored,
      requestClose,
      restrictionQuery,
      submit,
    }),
    [continueWithoutContract, currentScreen, editMode, markContractLoadErrored, requestClose, restrictionQuery, submit],
  );

  const derivedCtx = useMemo(
    () => ({ main, initialPackCodeResolved, optionalParts, prepareSubmitData }),
    [main, initialPackCodeResolved, optionalParts, prepareSubmitData],
  );

  return {
    canSeeAddToCartDisclaimer: capabilities.canSeeAddToCartDisclaimer,
    canSubmitToCartOrQuote: capabilities.canSubmitToCartOrQuote,
    currentScreen,
    derivedCtx,
    flowContextProps,
    form,
    isSending,
    productDescriptor,
    submit,
  };
}

export const AtcWizardProviders = ({
  children,
  derivedCtx,
  flowContextProps,
  form,
}: {
  children?: ReactNode;
  derivedCtx: AtcWizardProcessResult["derivedCtx"];
  flowContextProps: AtcWizardProcessResult["flowContextProps"];
  form: AtcWizardProcessResult["form"];
}) => {
  return (
    <AddToCartFlowProvider {...flowContextProps}>
      <FormProvider {...form}>
        <AddToCartDerivedContextProvider value={derivedCtx}>{children}</AddToCartDerivedContextProvider>
      </FormProvider>
    </AddToCartFlowProvider>
  );
};
